import { useState } from 'react'

import {
  type PlanTypeCardPartner,
  PlanTypeCardPartnerKind,
  PlanTypeCardPartnerSubmissionState,
  type PlanTypeCardPlanRecord,
} from '@ebalance/proto/bffuser/v1/submission_management_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Badge,
  Box,
  CardBody,
  Grid,
  HStack,
  Link,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import {
  FaAddressCardRegular,
  FaFileLinesRegularFull,
} from '@enechain/ecloud-icons'

import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import { iconSize } from '~/constants/iconSize'
import { formatKomaRanges } from '~/models/komaRange'
import { ContactModal } from '~/pages/SubmissionManagement/components/ContactModal'
import {
  type PlanRecordLink,
  buildPartnerCardUrl,
  findCounterpartyMismatch,
  resolvePlanRecordLink,
} from '~/pages/SubmissionManagement/models/partnerMismatch'
import type { PlanDetailMismatch } from '~/pages/SubmissionManagement/models/planDetailUrl'

/** JEPX は BFF が名称を返さず、kind で固定表示名を出す (proto doc)。 */
const JEPX_LABEL_KEYS: Partial<Record<PlanTypeCardPartnerKind, string>> = {
  [PlanTypeCardPartnerKind.JEPX_SPOT]:
    'page:submissionManagement.partner.jepxSpot',
  [PlanTypeCardPartnerKind.JEPX_INTRADAY]:
    'page:submissionManagement.partner.jepxIntraday',
}

/**
 * 提出状態行 (AC-PSM-034 / 035 / 036)。MATCHED は「一致」、MISMATCH は「不整合」badge +
 * 不整合範囲。UNSPECIFIED (判定材料なし) は何も出さない。badge は submissionState で決め、
 * 範囲は取引相手間ミスマッチ (kind = COUNTERPARTY) の要素から出す。計画内・計画間 (#5901)
 * の種別は要素ごとに additive に足す。
 */
const PartnerStateRow = ({
  partner,
}: {
  partner: PlanTypeCardPartner
}): React.ReactElement | null => {
  const { t } = useTranslation()
  if (partner.submissionState === PlanTypeCardPartnerSubmissionState.MATCHED) {
    return (
      <Text color="Text.positiveMid" fontSize="xs">
        {t('page:submissionManagement.partner.state.matched')}
      </Text>
    )
  }
  if (partner.submissionState !== PlanTypeCardPartnerSubmissionState.MISMATCH) {
    return null
  }
  const counterparty = findCounterpartyMismatch(partner)
  return (
    <HStack flexWrap="wrap" gap={2}>
      <Badge status="negative">
        {t('page:submissionManagement.partner.state.mismatch')}
      </Badge>
      {counterparty !== undefined && counterparty.ranges.length > 0 && (
        <Text textStyle="captionStrong" color="Text.negativeMid">
          {formatKomaRanges(counterparty.ranges)}
        </Text>
      )}
    </HStack>
  )
}

const PartnerCard = ({
  partner,
  link,
  side,
}: {
  partner: PlanTypeCardPartner
  link: PlanRecordLink | undefined
  side: PlanDetailMismatch['side']
}): React.ReactElement => {
  const { t } = useTranslation()
  const [isContactOpen, setIsContactOpen] = useState(false)
  const jepxKey = JEPX_LABEL_KEYS[partner.kind]
  const name =
    jepxKey !== undefined ? t(jepxKey) : (partner.displayName ?? undefined)
  // JEPX は業界全体の取引相手で個別の連絡先が無いため連絡先アイコンを出さない
  // (AC-CM-004)。JEPX の判定は固定表示名と同じ表に一本化する。分割供給計画の
  // カードは本画面に出ない。自社内コードは design どおり出す (未登録なら空状態)。
  const showContact = jepxKey === undefined
  const contactLabel = t('page:submissionManagement.contactModal.buttonLabel')

  const nameBlock = (
    <VStack alignItems="flex-start" gap={0} minWidth={0}>
      {name !== undefined && (
        <Text textStyle="captionStrong" wordBreak="break-all">
          {name}
        </Text>
      )}
      <Text color="Text.midEmphasis" fontSize="xs">
        {partner.counterpartyCode}
      </Text>
      <PartnerStateRow partner={partner} />
    </VStack>
  )
  const hasLink = link !== undefined
  const hasMismatch =
    partner.submissionState === PlanTypeCardPartnerSubmissionState.MISMATCH

  return (
    <HStack
      position="relative"
      justifyContent="space-between"
      gap={2}
      padding="8px 12px"
      // 不整合の視覚区分 (design .partner-box.has-mismatch)。
      background={hasMismatch ? 'Surface.negativeLow' : 'Surface.primary'}
      borderWidth="1px"
      borderColor={hasMismatch ? 'Border.negativeMid' : 'Border.lowEmphasis'}
      borderRadius="md"
      _hover={hasLink ? { background: 'Surface.secondary' } : undefined}
    >
      {hasLink ? (
        // [DS差異] DS Link の recipe (inline 装飾・リンク色・下線) を打ち消す。design は
        // 取引先カード全体がクリック対象のため、::after でカード全体にリンクの当たり
        // 判定を広げる (stretched link)。連絡先ボタンはリンクの外に置いて前面に出し、
        // <a> の中に <button> を入れない。
        <Link
          minWidth={0}
          color="inherit"
          textDecoration="none"
          _hover={{ textDecoration: 'none' }}
          _after={{ content: '""', position: 'absolute', inset: 0 }}
          asChild
        >
          <RouterLink
            rel="noopener noreferrer"
            target="_blank"
            to={buildPartnerCardUrl(link, partner, side)}
          >
            {nameBlock}
          </RouterLink>
        </Link>
      ) : (
        // 代表提出計画なしで提出記録なしの通知も無い (D4): リンクなし・クリック不可。
        nameBlock
      )}
      <HStack flexShrink={0} gap={1}>
        {showContact && (
          <IconOnlyButton
            position="relative"
            zIndex={1}
            label={contactLabel}
            onClick={() => setIsContactOpen(true)}
            size="xs"
            title={contactLabel}
            variant="ghost"
          >
            {/* [DS差異] design は電話 glyph だが ecloud-icons に無いため、
                旧需要調達計画一覧の連絡先ボタンと同じ address-card で代替する。 */}
            <FaAddressCardRegular boxSize={iconSize.gridInline} />
          </IconOnlyButton>
        )}
        {hasLink && (
          // 遷移は card 全体が担う単一リンク。icon は「開く」ことを示す装飾。
          <Box color="Text.midEmphasis" aria-hidden="true">
            <FaFileLinesRegularFull boxSize={iconSize.gridInline} />
          </Box>
        )}
      </HStack>
      {isContactOpen && (
        <ContactModal
          counterpartyCode={partner.counterpartyCode}
          counterpartyName={name ?? partner.counterpartyCode}
          onClose={() => setIsContactOpen(false)}
        />
      )}
    </HStack>
  )
}

const PartnerColumn = ({
  heading,
  side,
  partners,
  link,
}: {
  heading: string
  side: PlanDetailMismatch['side']
  partners: readonly PlanTypeCardPartner[]
  link: PlanRecordLink | undefined
}): React.ReactElement => (
  <VStack alignItems="stretch" gap={2}>
    <Text textStyle="captionStrong" color="Text.midEmphasis">
      {heading}
    </Text>
    {partners.map((partner) => (
      <PartnerCard
        key={`${partner.kind}-${partner.counterpartyCode}`}
        link={link}
        partner={partner}
        side={side}
      />
    ))}
  </VStack>
)

/**
 * 計画タイプカードの body (AC-PSM-033 / 034 / 038)。計画レコードグループ
 * (2 件以上の時だけ代表 BG コード見出し) → 構成事業者ブロック → 調達側 / 販売側
 * 2 カラムの取引先カード。取引先カードは提出状態 (一致 / 不整合 + 範囲) と連絡先アイコン
 * (AC-PSM-037 / AC-CM-001〜004) を出す。
 */
export const PlanTypeCardBody = ({
  planRecords,
  noSubmissionRecord,
  targetDate,
}: {
  planRecords: readonly PlanTypeCardPlanRecord[]
  /** カードが「eBalance での提出記録なし」(AC-PSM-078) か。版の無い記録のリンク先を決める */
  noSubmissionRecord: boolean
  /** ダッシュボードの対象日 (提出記録の無い計画の業務キー) */
  targetDate: Date
}): React.ReactElement => {
  const { t } = useTranslation()
  const showRecordHeading = planRecords.length >= 2

  return (
    <CardBody padding="16px">
      <VStack alignItems="stretch" gap={4}>
        {planRecords.map((record) => {
          const link = resolvePlanRecordLink(
            record,
            noSubmissionRecord,
            targetDate,
          )
          return (
            <VStack
              key={record.demandProcurementMasterId}
              alignItems="stretch"
              gap={3}
            >
              {showRecordHeading && (
                <Text textStyle="captionStrong">
                  {record.representativeBgCode}
                </Text>
              )}
              {record.partnerBlocks.map((block, blockIndex) => (
                <VStack
                  key={block.constituent?.businessCode ?? `block-${blockIndex}`}
                  alignItems="stretch"
                  gap={2}
                >
                  {block.constituent !== undefined && (
                    <Text textStyle="captionStrong">
                      {block.constituent.name}
                    </Text>
                  )}
                  <Grid gridGap={3} gridTemplateColumns="1fr 1fr">
                    <PartnerColumn
                      heading={t(
                        'page:submissionManagement.partner.procurementSide',
                      )}
                      link={link}
                      partners={block.procurementPartners}
                      side="procurement"
                    />
                    <PartnerColumn
                      heading={t('page:submissionManagement.partner.salesSide')}
                      link={link}
                      partners={block.salesPartners}
                      side="sales"
                    />
                  </Grid>
                </VStack>
              ))}
            </VStack>
          )
        })}
      </VStack>
    </CardBody>
  )
}
